/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { SAFETY_CONDITION_HELPERS_BY_AREA, SAFETY_QUESTIONS, TEST_MAP } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES, resolveMessage } from "./GSA_MYRA_MESSAGES";
import styles from "./SelfAssessment.module.css";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import { generatePayloadForScreen } from "./generatePayloadForScreen";
import MyraChatService from "../voice-agent/MyraChatService";

let _ssId = 80000;
function nextSsId() {
  return ++_ssId;
}
const SAFETY_CONDITION_MAP = {
  LBP: {
    BOWEL_BLADDER: "lbp-safety-bowel-bladder",
    SHOOTING_LEG_PAIN: "lbp-safety-shooting-leg",
    NUMBNESS_TINGLING: "lbp-safety-numbness",
    LEG_WEAKNESS: "lbp-safety-weakness",
  },
  HIP: {
    BOWEL_BLADDER: "hip-safety-bowel-bladder",
    SHOOTING_LEG_PAIN: "hip-safety-shooting-numbness",
    CHILDHOOD_HIP: "hip-safety-childhood",
    AVN: "hip-safety-avn",
    HIP_REPLACEMENT: "hip-safety-replacement",
    POST_TRAUMA: "hip-safety-trauma",
  },
  THIGH: {
    BOWEL_BLADDER: "thigh-safety-bowel-bladder",
    SHOOTING_LEG_PAIN: "thigh-safety-shooting-numbness",
    POST_TRAUMA: "thigh-safety-trauma",
    RECENT_SURGERY: "thigh-safety-surgery",
  },
  KNEE: {
    BOWEL_BLADDER: "knee-safety-bowel-bladder",
    NUMBNESS_TINGLING: "knee-safety-numbness",
    LEG_WEAKNESS: "knee-safety-weakness",
    RECENT_SURGERY: "knee-safety-surgery-recent",
    POST_TRAUMA: "knee-safety-trauma",
    UNTREATED_TORN_LIGAMENT: "knee-safety-ligament",
  },
};
let _historyId = 0;
function nextId() {
  return ++_historyId;
}

export default function SafetyScreeningScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop, readOnly = false, entry = null, showQuestionNuggetMessage }) {
  const problemArea = assessmentData.problemArea;
  const safetyData = SAFETY_QUESTIONS[problemArea];

  // In readOnly mode, seed checkedItems from the history entry so the
  // frozen checklist displays the user's original selections highlighted.
  // Live mode always starts empty.
  const initialChecked = readOnly && entry?.data?.selectedConditions ? new Set(entry.data.selectedConditions) : new Set();
  const [checkedItems, setCheckedItems] = useState(initialChecked);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);

  // ── FIX: was generatePayloadForScreen(SafetyScreeningScreen) — passed
  //    the component function instead of a string key. Now uses the correct
  //    call signature matching every other screen, and recomputes when
  //    assessmentData changes.

  // ── Screen status ──
  // 'checklist' → user is selecting conditions
  // 'waiting'   → conditions submitted, Claude is conducting follow-up conversation
  // 'advisory'  → SAFETY_ADVISORY received, user chooses Continue or Leave
  // 'stopped'   → SAFETY_STOP received or user chose Leave on advisory
  const [screenStatus, setScreenStatus] = useState("checklist");
  const [outcomeText, setOutcomeText] = useState("");
  const screenStatusRef = useRef("checklist");
  const checkedItemsRef = useRef([]);

  // Keep ref in sync for use inside command handlers
  useEffect(() => {
    screenStatusRef.current = screenStatus;
  }, [screenStatus]);

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    if (!safetyData) return;
    let cancelled = false;
    async function init() {
      if (!readOnly) {
        await showMyraMessage(MYRA_MESSAGES.safety.SAFETY_SETUP_BEAT, 800);
        await showQuestionNuggetMessage(MYRA_MESSAGES.safety.SAFETY_QUESTION, MYRA_MESSAGES.safety.SAFETY_NUGGET_BY_AREA[problemArea], 900);
      }
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    window.currentFlow = "flow_safety";
    VoiceService._currentFlow = "flow_safety";
    if (window.mayraCode) {
      VoiceService.startFlowWithoutAnnouncement("flow_safety");
    }
  }, []);

  // ── Voice boilerplate ──
  useScreenVoice({
    screenName: "SafetyScreeningScreen",
    trackerName: "safety_screening",
    flowName: "flow_safety",
    payloadKey: "SafetyScreeningScreen",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value) => {
        if (value === "continue_command") handleSubmit();
      },
      MOVE_ON: () => handleSubmit(),
      SAFETY_CONTINUE: (value) => {
        // gtag conversion
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
            value: 1.0,
            currency: "USD",
          });
        }
        const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
        const test = TEST_MAP[subtype];
        const testName = test?.name || "";
        pushToHistory({ type: "divider", id: nextId() });
        pushToHistory({ type: "section-header", label: "Movement Test 1", id: nextId() });
        const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
        if (testTransitionTemplate) {
          const testTransition = resolveMessage(testTransitionTemplate, { testName });
          setTimeout(async () => {
            await showMyraMessage(testTransition, 800);
          }, 10);
        }
        setTimeout(async () => {
          const testTransitionTemplate2 = MYRA_MESSAGES.preNprs?.voiceHeadsUp;
          let testTransition2 = resolveMessage(testTransitionTemplate2, { testName });
          await showMyraMessage(testTransition2, 900);
        }, 10);
        // pushToHistory({ type: "frozen-widget", widgetType: "voice_warning", id: nextId(), data: {} });
        // pushToHistory({ type: "divider", id: nextId() });
        onContinue({
          safetyScreenResult: "CLEAR",
          safetyScreenSelection: checkedItemsRef.current,
          safetyOutcome: "CONTINUE",
          completed_safety_screen: true,
          selected_pain_location: true,
          started_safety_screen: true,
          safety_condition_selected: true,
        });
      },

      SAFETY_STOP: (value) => {
        // Add debounce to prevent double execution
        if (window._safetyStopExecuted) return;
        window._safetyStopExecuted = true;

        setOutcomeText(value || "");
        setScreenStatus("stopped");
        screenStatusRef.current = "stopped";

        // Push stop card to conversation history
        pushToHistory({
          type: "frozen-widget",
          widgetType: "safety_stop_card",
          id: nextSsId(),
          data: { detail: value || "" },
        });

        const followUp = MYRA_MESSAGES.safetyOutcome?.stoppedFollowUp || "I know this isn't the news you were hoping for. If you have any questions, I'm right here.";
        showMyraMessage(followUp, 800);

        // Reset after 2 seconds
        setTimeout(() => {
          window._safetyStopExecuted = false;
        }, 2000);
      },

      SAFETY_ADVISORY: (value) => {
        setOutcomeText(value || "");
        setScreenStatus("advisory");
        screenStatusRef.current = "advisory";
      },
      SELECT_SAFETY_CONDITION: (value, speech, intent, extracted, actionValue, actions) => {
        // actions is an array — may contain one or more SELECT_SAFETY_CONDITION entries
        const areaMap = SAFETY_CONDITION_MAP[problemArea];
        if (!areaMap) return; // unknown problem area — silently ignore

        // Collect all values from the actions array (handles case 3: multiples)
        const valuesToApply = (actions || []).filter((a) => a.action === "SELECT_SAFETY_CONDITION").map((a) => a.value);

        valuesToApply.forEach((enumValue) => {
          const conditionId = areaMap[enumValue];
          if (!conditionId) return; // no mapping for this area — silently ignore
          toggleItem(conditionId);
        });
      },
    },
    exitReason: "safety_screening_completed",
  });

  // ── Command registration ──
  useEffect(() => {
    window.currentCommandsAudio = {
      // Voice/chat "continue" during checklist phase only
      ANSWER_MidJourneyFlow_QUESTION: (value) => {
        if (window.exitHandlerOpen) return;
        if (screenStatusRef.current !== "checklist") return;
        if (value === "continue_command") handleSubmitRef.current();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (screenStatusRef.current !== "checklist") return;
        handleSubmitRef.current();
      },

      // ── Safety outcome actions from backend ──

      SAFETY_CONTINUE: (value) => {
        // gtag conversion
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
            value: 1.0,
            currency: "USD",
          });
        }
        const checked = [...checkedItems];
        const balanceId = `${problemArea.toLowerCase()}-safety-balance`;
        const balanceValue = checked.includes(balanceId) ? "unsteady" : "steady";
        const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
        const test = TEST_MAP[subtype];
        const testName = test?.name || "";
        pushToHistory({ type: "divider", id: nextId() });
        pushToHistory({ type: "section-header", label: "Movement Test 1", id: nextId() });
        const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
        if (testTransitionTemplate) {
          const testTransition = resolveMessage(testTransitionTemplate, { testName });
          setTimeout(async () => {
            await showMyraMessage(testTransition, 800);
          }, 10);
        }
        setTimeout(async () => {
          const testTransitionTemplate2 = MYRA_MESSAGES.preNprs?.voiceHeadsUp;
          let testTransition2 = resolveMessage(testTransitionTemplate2, { testName });
          await showMyraMessage(testTransition2, 900);
        }, 10);
        // pushToHistory({ type: "frozen-widget", widgetType: "voice_warning", id: nextId(), data: {} });
        // pushToHistory({ type: "divider", id: nextId() });
        onContinue({
          safetyScreenResult: "CLEAR",
          safetyScreenSelection: checkedItemsRef.current,
          safetyOutcome: "CONTINUE",
          completed_safety_screen: true,
          selected_pain_location: true,
          balance: balanceValue,
          started_safety_screen: true,
          safety_condition_selected: true,
        });
      },

      SAFETY_STOP: (value) => {
        setOutcomeText(value || "");
        setScreenStatus("stopped");
        screenStatusRef.current = "stopped";

        // Push stop card to conversation history
        pushToHistory({
          type: "frozen-widget",
          widgetType: "safety_stop_card",
          id: nextSsId(),
          data: { detail: value || "" },
        });

        // Follow-up message — Myra stays available
        const followUp = MYRA_MESSAGES.safetyOutcome?.stoppedFollowUp || /* PLACEHOLDER COPY */ "I know this isn't the news you were hoping for. If you have any questions, I'm right here.";
        showMyraMessage(followUp, 800);
      },

      SAFETY_ADVISORY: (value) => {
        setOutcomeText(value || "");
        setScreenStatus("advisory");
        screenStatusRef.current = "advisory";
      },
      SELECT_SAFETY_CONDITION: (value, speech, intent, extracted, actionValue, actions) => {
        // actions is an array — may contain one or more SELECT_SAFETY_CONDITION entries
        const areaMap = SAFETY_CONDITION_MAP[problemArea];
        if (!areaMap) return; // unknown problem area — silently ignore

        // Collect all values from the actions array (handles case 3: multiples)
        const valuesToApply = (actions || []).filter((a) => a.action === "SELECT_SAFETY_CONDITION").map((a) => a.value);

        valuesToApply.forEach((enumValue) => {
          const conditionId = areaMap[enumValue];
          if (!conditionId) return; // no mapping for this area — silently ignore
          toggleItem(conditionId);
        });
      },
    };
  }, []);

  // ── Stable ref for handleSubmit (avoids stale closure in commands) ──
  const handleSubmitRef = useRef(null);

  // ── Toggle a safety item ──
  const toggleItem = useCallback(
    (id) => {
      if (locked || readOnly) return;
      setCheckedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [locked],
  );

  // ── Submit checklist ──
  // FIX: Previously, when items were checked, handleSubmit prematurely
  // pushed a safety_stop_card and set screenStatus to "stopped" BEFORE
  // Claude evaluated the selections. It then overwrote to "waiting",
  // leaving the user stuck with a stop message and no way forward.
  //
  // Correct flow:
  //   1. Push frozen checklist + user echo to history
  //   2. Show Myra transition message
  //   3. Send updated payload (with checked conditions) to backend
  //   4. Enter "waiting" — Claude conducts follow-up, then sends
  //      SAFETY_CONTINUE, SAFETY_ADVISORY, or SAFETY_STOP
  const handleSubmit = useCallback(async () => {
    if (locked || readOnly) return;
    setLocked(true);

    const checked = [...checkedItems];
    const conditionsList = safetyData.conditions;
    const balanceId = `${problemArea.toLowerCase()}-safety-balance`;
    const balanceValue = checked.includes(balanceId) ? "unsteady" : "steady";

    // No items checked → clear, continue immediately
    const nonBalanceChecked = checked.filter((id) => !id.endsWith("-safety-balance"));
    if (nonBalanceChecked.length === 0) {
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
          value: 1.0,
          currency: "USD",
        });
      }
      pushToHistory({
        type: "safety-summary",
        id: nextSsId(),
        data: {
          selectedConditions: checked,
          conditionsList: conditionsList,
        },
      });
      if (checked.filter((id) => id.endsWith("-safety-balance")).length > 0) {
        pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "balance_only", labels: "Challenges with balance while standing", selectedConditions: [balanceId] } }, { type: "user", text: "Challenges with balance while standing", id: nextSsId() });
      } else {
        pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "none", labels: "" } }, { type: "user", text: "None of these apply", id: nextSsId() });
      }

      await showMyraMessage(MYRA_MESSAGES.safety?.clearNormal || "Good — we're clear to continue. Let me ask about the character of your pain.", 800);
      window.notOpenQNA = false;

      const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
      const test = TEST_MAP[subtype];
      const testName = test?.name || "";
      pushToHistory({ type: "divider", id: nextId() });
      pushToHistory({ type: "section-header", label: "Movement Test 1", id: nextId() });
      const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
      if (testTransitionTemplate) {
        const testTransition = resolveMessage(testTransitionTemplate, { testName });
        await showMyraMessage(testTransition, 800);
      }
      const testTransitionTemplate2 = MYRA_MESSAGES.preNprs?.voiceHeadsUp;
      let testTransition2 = resolveMessage(testTransitionTemplate2, { testName });
      await showMyraMessage(testTransition2, 900);
      // pushToHistory({ type: "frozen-widget", widgetType: "voice_warning", id: nextId(), data: {} });
      // pushToHistory({ type: "divider", id: nextId() });

      onContinue({
        safetyScreenResult: "CLEAR",
        safetyScreenSelection: [],
        safetyOutcome: "CONTINUE",
        completed_safety_screen: true,
        selected_pain_location: true,
        started_safety_screen: true,
        safety_condition_selected: balanceValue ? true : false,
        balance: balanceValue,
      });
      return;
    }

    // ── Items checked → hand off to Claude for evaluation ──

    // Build labels for user echo
    const labels = checked.map((id) => safetyData.conditions.find((c) => c.id === id)?.label).filter(Boolean);

    // Push frozen safety-summary (full visual checklist with checked items
    // highlighted) + user echo. Mirrors the no-selection path's pattern so
    // the widget stays visible in history during the Claude follow-up.
    pushToHistory(
      {
        type: "safety-summary",
        id: nextSsId(),
        data: {
          selectedConditions: checked,
          conditionsList: conditionsList,
        },
      },
      { type: "user", text: labels.join(", "), id: nextSsId() },
    );

    // Store checked items for use in command handlers
    checkedItemsRef.current = checked;

    // Myra transition message — tells the user Claude will follow up
    await showMyraMessage(MYRA_MESSAGES.safety?.followUpIntro || "Thanks for letting me know. I have a couple of follow-up questions to make sure it's safe to continue.", 800);

    // Send updated payload to backend with the user's checked conditions.
    // This lets Claude see what the user selected and conduct an informed
    // follow-up conversation before deciding the safety outcome.
    const payloadWithSelections = generatePayloadForScreen("SafetyScreeningScreen", {
      ...assessmentData,
      safetySelections: labels,
      safetySelectionIds: checked,
    });
    if (VoiceService?._ws?.readyState === WebSocket.OPEN) {
      VoiceService.sendMidJourneyData(payloadWithSelections);
    }

    // Enter waiting state — Claude conducts conversational follow-up.
    // The screen component returns null (no live UI), but stays mounted
    // so command handlers (SAFETY_CONTINUE, SAFETY_ADVISORY, SAFETY_STOP)
    // remain active. The conversation continues in the voice/chat layer.
    // The final SAFETY_* command from Claude triggers the matching handler.
    setScreenStatus("waiting");
    screenStatusRef.current = "waiting";

    if (MyraChatService) {
      window.notOpenQNA = true;
      window.safetySelections = labels;
      window.safetySelectionIds = checked;
      try {
        // Send a message to Claude/backend about the selected symptoms
        const symptomMessage = `I've these symptoms: ${labels.join(", ")}. Please ask follow-up questions to evaluate safety.`;
        const newHistory = [{ role: "user", content: symptomMessage }];

        const response = await MyraChatService.sendMessage(
          symptomMessage,
          window.trSessionId,
          newHistory,
          0, // turn number
          payloadWithSelections,
          "SafetyScreeningScreen",
        );
        const responseText = response?.response || response?.speech || "I need to ask a few follow-up questions to understand your situation better.";
        await showMyraMessage(responseText, 900);
      } catch (err) {
        console.error("Failed to send symptom message:", err);
      }
    }

    VoiceService.handleSafetyScreenFollowUp();
    // pushToHistory({
    //   type: "frozen-widget",
    //   widgetType: "safety_stop_card",
    //   id: nextSsId(),
    //   data: {
    //     detail: `Based on your selection (${labels.join(", ")}), it's not safe to continue with the assessment at this time. Please consult with a healthcare provider.`,
    //     selectedConditions: labels,
    //     triggeredBy: "manual_selection",
    //   },
    // });
  }, [checkedItems, locked, safetyData, assessmentData, onContinue, pushToHistory, showMyraMessage]);

  // Keep handleSubmitRef current
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // ── Advisory: user chooses Continue ──
  const handleAdvisoryContinue = useCallback(() => {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
        value: 1.0,
        currency: "USD",
      });
    }
    const checked = [...checkedItems];
    const balanceId = `${problemArea.toLowerCase()}-safety-balance`;
    const balanceValue = checked.includes(balanceId) ? "unsteady" : "steady";
    window.notOpenQNA = false;
    const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
    const test = TEST_MAP[subtype];
    const testName = test?.name || "";
    pushToHistory({ type: "divider", id: nextId() });
    pushToHistory({ type: "section-header", label: "Movement Test 1", id: nextId() });
    const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
    if (testTransitionTemplate) {
      const testTransition = resolveMessage(testTransitionTemplate, { testName });
      setTimeout(async () => {
        await showMyraMessage(testTransition, 800);
      }, 10);
    }
    setTimeout(async () => {
      const testTransitionTemplate2 = MYRA_MESSAGES.preNprs?.voiceHeadsUp;
      let testTransition2 = resolveMessage(testTransitionTemplate2, { testName });
      await showMyraMessage(testTransition2, 900);
    }, 10);
    // pushToHistory({ type: "frozen-widget", widgetType: "voice_warning", id: nextId(), data: {} });
    // pushToHistory({ type: "divider", id: nextId() });
    onContinue({
      safetyScreenResult: "CLEAR",
      safetyScreenSelection: checkedItemsRef.current,
      safetyOutcome: "ADVISORY_CONTINUE",
      completed_safety_screen: true,
      selected_pain_location: true,
      balance: balanceValue,
      started_safety_screen: true,
      safety_condition_selected: true,
    });
  }, [onContinue]);

  // ── Advisory: user chooses Leave ──
  const handleAdvisoryLeave = useCallback(() => {
    setScreenStatus("stopped");
    screenStatusRef.current = "stopped";

    // Push frozen card so the advisory text stays in history
    pushToHistory({
      type: "frozen-widget",
      widgetType: "safety_advisory_card",
      id: nextSsId(),
      data: { detail: outcomeText, choice: "leave" },
    });

    const leaveMsg = MYRA_MESSAGES.safetyOutcome?.advisoryLeave || /* PLACEHOLDER COPY */ "No problem at all. When you're ready to give it a try, I'll be right here.";
    showMyraMessage(leaveMsg, 800);
    setTimeout(() => {
      window.location.href = "/?gsa=v2";
    }, 3000);
  }, [outcomeText, pushToHistory, showMyraMessage]);

  // ── No safety data fallback ──
  useEffect(() => {
    if (!safetyData) {
      onContinue({
        safetyScreenResult: "CLEAR",
        safetyScreenSelection: [],
        safetyOutcome: "CONTINUE",
        completed_safety_screen: true,
        selected_pain_location: true,
        started_safety_screen: true,
        safety_condition_selected: false,
      });
    }
  }, [safetyData, onContinue]);

  if (!safetyData) return null;
  if (!ready) return null;

  // ── Waiting: no live UI needed, conversation happening via voice/chat ──
  if (screenStatus === "waiting") return null;

  // ── Stopped: no live UI needed, stop card + follow-up are in history ──
  if (screenStatus === "stopped") return null;

  // ── Advisory: show card with Continue / Leave ──
  if (screenStatus === "advisory") {
    return (
      <div className={styles.inlineUi}>
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderRadius: "0.75rem",
            padding: "1.25rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⚠️</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#1f2937",
              marginBottom: "0.75rem",
            }}
          >
            {MYRA_MESSAGES.safetyOutcome?.advisoryHeading || /* PLACEHOLDER COPY */ "Something to consider before continuing"}
          </div>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#4b5563",
              lineHeight: 1.5,
              marginBottom: "1.25rem",
            }}
          >
            {outcomeText}
          </p>

          {/* Continue — primary action */}
          <button
            style={{
              width: "100%",
              background: "#0d9488",
              color: "white",
              fontWeight: "bold",
              fontSize: "16px",
              padding: "0.875rem",
              borderRadius: "0.5rem",
              border: "none",
              cursor: "pointer",
              marginBottom: "0.625rem",
            }}
            onClick={handleAdvisoryContinue}
          >
            Continue with assessment
          </button>

          {/* Leave — secondary action */}
          <button
            style={{
              width: "100%",
              background: "transparent",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: "15px",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
            onClick={handleAdvisoryLeave}
          >
            I'll come back later
          </button>
        </div>
      </div>
    );
  }

  // ── Checklist phase ──
  const checkSvg = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
  return (
    <div className={`${styles.inlineUi2} ${styles.safetyContainer}`}>
      {!locked ? (
        <>
          <div className={styles.SafetyChipPairStack}>
            {safetyData.conditions.map((c) => {
              const isChecked = checkedItems.has(c.id);

              return (
                <div key={c.id} className={styles.chipPair}>
                  {/* Clickable chip */}
                  <div
                    disabled={locked || readOnly}
                    className={`
                    ${styles.optionCard}
                    ${isChecked ? styles.selected : ""}
                    ${locked ? styles.locked : ""}
                  `}
                    onClick={() => !readOnly && toggleItem(c.id)}
                  >
                    {c.label}
                  </div>
                  {SAFETY_CONDITION_HELPERS_BY_AREA?.[problemArea]?.[c.id] && <div className={styles.chipHelper}>{SAFETY_CONDITION_HELPERS_BY_AREA[problemArea][c.id]}</div>}
                  {/* Optional helper text (if exists in your data) */}
                </div>
              );
            })}
          </div>
          {/* <p className={styles.safetyHelper}>If none of these apply to you, just tap Continue</p> */}
          <div className={styles.safetyContinueBtnWrapper}>
            <button className={styles.safetyContinue} onClick={handleSubmit} disabled={locked || readOnly}>
              Continue
            </button>
          </div>
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}
